import { useEffect, useState } from 'react';
import { API_URL } from '../../config/api';
import { readApiJson } from '../../utils/apiErrors';

// ── ManageLegalApiModal — opened from the 🔌 API button in Manage Legal
// Master. Lets an admin generate/rotate/revoke the API key an external
// system (e.g. a Power Automate flow triggered from a PowerPoint add-in)
// uses to POST legal record JSON straight into this table, without ever
// opening a browser session — see kosmos_app/legal_views/webhook.py and
// api_credential.py on the backend. The raw key is only ever shown once,
// right after Generate/Rotate — refreshing or reopening this modal only
// shows metadata (label, created by/when, last used), never the key itself,
// since only its hash is stored server-side.

const WEBHOOK_PATH = '/api/legal/webhook/';

const EXAMPLE_PAYLOAD = JSON.stringify([
    { entity_name: 'Acme Corp', country: 'US', requestor_name: 'Jane Doe', document_category: 'NDA', received_on: '2026-09-20' },
    { id: 283, additional_comments: 'Updated via Power Automate' },
], null, 2);

function CopyButton({ value, label = 'Copy' }) {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Clipboard access can fail (permissions, non-secure context) —
            // the value is still selectable/visible in the field next to
            // this button, so there's a manual fallback either way.
        }
    };
    return (
        <button type="button" className="legal-btn-cancel legal-cat-inline-btn" onClick={copy}>
            {copied ? 'Copied ✓' : label}
        </button>
    );
}

function ManageLegalApiModal({ canManageData = false, onClose }) {
    const [credential, setCredential] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [newKey, setNewKey] = useState(null);
    const [label, setLabel] = useState('');

    const load = async () => {
        setLoading(true); setError('');
        try {
            const res = await fetch(`${API_URL}/api/legal/api-credential/`, { credentials: 'include' });
            const body = await readApiJson(res, 'Could not load the API key status.');
            setCredential(body.credential || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const postAction = async (action) => {
        setBusy(true); setError('');
        try {
            const res = await fetch(`${API_URL}/api/legal/api-credential/`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
                body: JSON.stringify({ action, label: label.trim() }),
            });
            const body = await readApiJson(res, 'Request failed.');
            setCredential(body.credential || null);
            setNewKey(body.api_key || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const rotate = () => {
        if (credential && !window.confirm('This replaces the current key — anything still using the old one will stop working immediately. Continue?')) return;
        postAction('rotate');
    };

    const revoke = () => {
        if (!window.confirm('Revoke the API key? Any integration using it will stop working until a new key is generated.')) return;
        postAction('revoke').then(() => setNewKey(null));
    };

    if (!canManageData) return null;

    const webhookUrl = `${API_URL}${WEBHOOK_PATH}`;

    return (
        <div className="legal-modal-bg" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="legal-modal legal-modal-wide">
                <div className="legal-modal-hdr">
                    <div className="legal-modal-icon">🔌</div>
                    <div>
                        <div className="legal-modal-title">API Access</div>
                        <div className="legal-modal-sub">Let an external system (e.g. Power Automate) push legal records in as JSON</div>
                    </div>
                    <button className="legal-modal-close" onClick={onClose} style={{ marginLeft: 'auto' }}>✕</button>
                </div>
                <div className="legal-modal-body">
                    {error && <div className="legal-form-error">{error}</div>}

                    <div className="legal-field" style={{ marginBottom: 14 }}>
                        <label>Webhook URL</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input className="legal-cat-inline-input" style={{ flex: 1 }} readOnly value={webhookUrl} onFocus={e => e.target.select()} />
                            <CopyButton value={webhookUrl} />
                        </div>
                    </div>

                    {loading ? <p style={{ color: '#65676b' }}>Loading…</p> : (
                        <>
                            {newKey && (
                                <div className="legal-field" style={{ marginBottom: 14 }}>
                                    <label>New API key — copy it now, it won't be shown again</label>
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <input className="legal-cat-inline-input" style={{ flex: 1, fontFamily: 'monospace' }} readOnly value={newKey} onFocus={e => e.target.select()} />
                                        <CopyButton value={newKey} />
                                    </div>
                                </div>
                            )}

                            {credential ? (
                                <div className="legal-field" style={{ marginBottom: 14 }}>
                                    <label>Current key</label>
                                    <div style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.6 }}>
                                        <strong>{credential.label}</strong> — created by {credential.created_by || 'unknown'}
                                        {credential.create_date && ` on ${new Date(credential.create_date).toLocaleString()}`}
                                        <br />
                                        Last used: {credential.last_used_date ? new Date(credential.last_used_date).toLocaleString() : 'never'}
                                    </div>
                                </div>
                            ) : (
                                <p style={{ fontSize: 12.5, color: '#65676b', marginBottom: 14 }}>No API key yet — generate one below to enable the webhook.</p>
                            )}

                            <div className="legal-cat-toolbar" style={{ marginBottom: 14 }}>
                                <input
                                    className="legal-search legal-cat-search"
                                    placeholder="Label (e.g. Power Automate — PowerPoint)"
                                    value={label}
                                    onChange={e => setLabel(e.target.value)}
                                />
                                <button type="button" className="legal-btn-save legal-cat-add-btn" disabled={busy} onClick={rotate}>
                                    {busy ? '…' : credential ? 'Rotate Key' : 'Generate Key'}
                                </button>
                                {credential && (
                                    <button type="button" className="legal-btn-cancel legal-cat-inline-btn" disabled={busy} onClick={revoke}>
                                        Revoke
                                    </button>
                                )}
                            </div>

                            <div className="legal-field">
                                <label>How it works</label>
                                <ul style={{ fontSize: 12.5, color: '#475569', lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
                                    <li>POST JSON to the webhook URL above with header <code>X-Api-Key: &lt;your key&gt;</code>.</li>
                                    <li>Send either one record object, or an array of them, in the request body.</li>
                                    <li>Omit <code>id</code> to create a new record; include the <code>id</code> from a prior response to update it.</li>
                                    <li>Accepted fields: entity_name, country, requestor_name, finance_poc, document_category, document_subcategory, received_on (YYYY-MM-DD), current_status_comments, additional_comments.</li>
                                    <li>Status fields (Legal/Finance/Signatory/Current Status) aren't accepted here — those change through the Timeline instead.</li>
                                </ul>
                            </div>

                            <div className="legal-field" style={{ marginTop: 10 }}>
                                <label>Example payload</label>
                                <pre style={{ fontSize: 11, background: '#f8fafd', border: '1px solid #dddfe2', borderRadius: 8, padding: 10, overflowX: 'auto' }}>{EXAMPLE_PAYLOAD}</pre>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

export { ManageLegalApiModal };
export default ManageLegalApiModal;
